// Jornaya (LeadiD) + TrustedForm consent-certification scripts.
//
// Both vendors watch the form and write their identifier into a hidden input
// that the page must keep mounted: name="leadid_token" for Jornaya, and
// name="xxTrustedFormCertUrl" / name="xxTrustedFormPingUrl" for TrustedForm.
// We read those inputs at submit time and send the values with the lead.
//
// Kept in one place because three separate lead forms load these — the
// campaign id must never drift between them.

export const LEADID_CAMPAIGN_KEY = '4f92abea-9576-ff28-406d-190d2255539f'
const LEADID_ACCOUNT_KEY = 'F687F0C3-5166-FEF0-1329-1B967270DE02'

// Field names the vendor scripts write into. TrustedForm creates its own
// hidden inputs with these names inside every form it finds, so a page can end
// up with more than one input per name — see readCertificationFields().
export const TRUSTED_FORM_CERT_FIELD = 'xxTrustedFormCertUrl'
export const TRUSTED_FORM_PING_FIELD = 'xxTrustedFormPingUrl'
export const LEADID_FIELD = 'leadid_token'

const LEADID_SCRIPT_ID = 'LeadiDscript_campaign'
const TRUSTED_FORM_SCRIPT_ID = 'trustedform_script'

/**
 * Injects both certification scripts plus the Jornaya <noscript> fallback.
 *
 * Idempotent: the scripts stay for the life of the page. They must NOT be torn
 * down on unmount — TrustedForm observes the visitor's whole session to build
 * the certificate, and removing/re-adding the tag (React StrictMode remounts,
 * route changes) restarts that observation and can leave the certificate field
 * empty at submit time.
 */
export function injectLeadCertificationScripts(): void {
  if (!document.getElementById(LEADID_SCRIPT_ID)) {
    // snippet_version=2 populates the leadid_token input directly, so no
    // callback global is needed.
    const leadIdScript = document.createElement('script')
    leadIdScript.id = LEADID_SCRIPT_ID
    leadIdScript.type = 'text/javascript'
    leadIdScript.async = true
    leadIdScript.src = `//create.lidstatic.com/campaign/${LEADID_CAMPAIGN_KEY}.js?snippet_version=2`
    document.body.appendChild(leadIdScript)

    // Mirrors the vendor's <noscript> tag: a tracking pixel for visitors with
    // JavaScript disabled.
    const noscript = document.createElement('noscript')
    const pixel = document.createElement('img')
    pixel.src = `//create.leadid.com/noscript.gif?lac=${LEADID_ACCOUNT_KEY}&lck=${LEADID_CAMPAIGN_KEY}&snippet_version=2`
    pixel.alt = ''
    noscript.appendChild(pixel)
    document.body.appendChild(noscript)
  }

  if (!document.getElementById(TRUSTED_FORM_SCRIPT_ID)) {
    // ping_field is part of the current TrustedForm snippet: it yields a ping
    // URL that can be used to verify a certificate before claiming it. Without
    // it the buyer only ever sees the cert URL.
    const tfScript = document.createElement('script')
    tfScript.id = TRUSTED_FORM_SCRIPT_ID
    tfScript.type = 'text/javascript'
    tfScript.async = true
    tfScript.src =
      'https://api.trustedform.com/trustedform.js' +
      `?field=${encodeURIComponent(TRUSTED_FORM_CERT_FIELD)}` +
      `&ping_field=${encodeURIComponent(TRUSTED_FORM_PING_FIELD)}` +
      '&provide_referrer=false' +
      `&l=${new Date().getTime()}${Math.random()}`
    document.body.appendChild(tfScript)
  }
}

// Reads a vendor field by name, returning the first NON-EMPTY value.
//
// This must scan every matching element, not just the first. TrustedForm
// appends its own hidden input to each <form> it discovers rather than reusing
// ours, so document.getElementsByName(...)[0] frequently resolves to our empty
// placeholder while the real certificate sits in a sibling input — which is
// exactly how a lead posts with a blank certificate URL.
export function readVendorField(name: string): string {
  const nodes = document.getElementsByName(name)
  for (let i = 0; i < nodes.length; i++) {
    const value = (nodes[i] as HTMLInputElement).value?.trim()
    if (value) return value
  }
  return ''
}

export interface CertificationFields {
  leadIdToken: string
  trustedFormUrl: string
  trustedFormPingUrl: string
}

/** Current consent-certification values, or '' for any not yet issued. */
export function readCertificationFields(): CertificationFields {
  return {
    leadIdToken: readVendorField(LEADID_FIELD),
    trustedFormUrl: readVendorField(TRUSTED_FORM_CERT_FIELD),
    trustedFormPingUrl: readVendorField(TRUSTED_FORM_PING_FIELD),
  }
}
